<script setup lang="ts">
import {
  formatLocalDate,
  type Money,
  parseLocalDate,
  requireCurrencyCode,
  systemClock,
  todayUtc,
} from '@taler/domain'
import { ActionButton, Card, Select, StatusTag } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import type { SourceAccountOption } from '../internal/source-accounts.ts'
import type { Loan, RecordLoanPaymentInput } from '../internal/types.ts'

const props = defineProps<{
  loan: Loan
  sourceAccounts: readonly SourceAccountOption[]
  isSubmitting: boolean
  errorMessage: string | undefined
}>()
const emit = defineEmits<{ record: [input: RecordLoanPaymentInput] }>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')
const loanCurrency = computed(() => requireCurrencyCode(props.loan.currency))
const amount = ref<Money | null>(null)
const date = ref('')
const MIN_DATE = '1900-01-01'
const todayDate = formatLocalDate(todayUtc(systemClock))
const sourceAccountId = ref('')
const validationError = ref<string | undefined>(undefined)

function reset(): void {
  amount.value = null
  date.value = ''
  sourceAccountId.value = ''
  validationError.value = undefined
}

function submit(): void {
  validationError.value = undefined
  if (sourceAccountId.value === '' || amount.value === null) return
  try {
    emit('record', {
      amount: amount.value,
      date: parseLocalDate(date.value),
      sourceAccountId: sourceAccountId.value,
    })
  } catch {
    validationError.value = t('loans.invalidAmount')
  }
}

watch(
  () => props.isSubmitting,
  (submitting, wasSubmitting) => {
    if (wasSubmitting && !submitting && props.errorMessage === undefined) reset()
  },
)

const combinedError = computed(() => validationError.value ?? props.errorMessage)
const sourceAccountOptions = computed(() =>
  props.sourceAccounts.map((account) => ({ value: account.id, label: account.label })),
)
</script>

<template>
  <Card>
    <form class="flex flex-col items-start gap-3" @submit.prevent="submit">
      <h3 class="text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]">
        {{ t('loans.recordPayment') }}
      </h3>

      <div class="grid grid-cols-2 gap-[var(--space-cell)]">
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('loans.paymentAmount')
          }}</span>
          <AmountInput v-model="amount" :currency="loanCurrency" :locale="currentLocale" required />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('loans.paymentDate')
          }}</span>
          <input v-model="date" type="date" required :min="MIN_DATE" :max="todayDate" class="field-control" />
        </label>
      </div>

      <label class="flex w-full flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('loans.sourceAccount')
        }}</span>
        <Select
          v-model="sourceAccountId"
          :options="sourceAccountOptions"
          :placeholder="t('loans.selectSourceAccount')"
        />
      </label>

      <StatusTag v-if="combinedError" tone="danger">{{ combinedError }}</StatusTag>

      <ActionButton type="submit" :disabled="isSubmitting">
        {{ isSubmitting ? t('loans.recording') : t('loans.recordPayment') }}
      </ActionButton>
    </form>
  </Card>
</template>
